import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { SpeakingIcon } from '../TipSheetIcons/speaking-icon';
import { useCallback, useState } from 'react';
import { CloseIcon } from '../TipSheetIcons/close-icon';
import { StoriesSkeleton } from './Skeleton';

const Tooltip = styled.div`
  display: none;
  position: absolute;
  background-color: #d9d9d9;
  border: 1px solid #0a819f;
  font-size: 0.625rem;
  padding: 1rem;
  border-radius: 0.25rem;
  z-index: 1;
  width: 20rem;

  & ul {
    list-style-type: none;
    padding: 0;
    margin: 0;

    & li {
      margin-bottom: 0.25rem;
      font-style: italic;
    }

    & li:nth-child(n + 4) {
      display: none;
    }
  }
`;

const AbbreviationText = styled.span`
  margin-right: 0.25rem;
  position: relative;

  &:hover {
    ${Tooltip} {
      display: block;
      width: 6.25rem;
      color: #0e4c60;
      text-align: center;
      padding: 0.5rem 0;
      left: 50%;
      top: 2rem;
      transform: translateX(-50%);
    }
  }

  & .vote-not-available {
    &::after {
      color: #fcd70b;
      font-weight: bold;
      content: '  VOTE RESULTS PENDING  ';
    }
  }

  & .vote-pass-card {
    &::after {
      color: #fcd70b;
      font-weight: bold;
      content: '  \u2714  ';
    }
  }

  & .vote-fail-card {
    &::after {
      color: #fcd70b;
      font-weight: bold;
      content: '  \u2718  ';
    }
  }
`;

const CardContainer = styled.div`
  display: flex;
  border-radius: 0.875rem;
  position: relative;
`;

const CardContents = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0.5rem 1rem 2rem;
  z-index: 1;
  border-radius: 0.875rem;
  box-shadow: 3px 4px 4px 0px #00000040;
  background: ${(props) => props.$background || '#0e4c60'};
  border: ${(props) => props.$border || '1px solid #0e4c60'};
  height: 15.75rem;
`;

const CardDetails = styled.div`
  font-style: italic;
  font-size: 0.75rem;
  line-height: 1.1;
  color: ${(props) => props.$text || '#ffffff'};
  border-top: ${(props) => props.$border || '1px dashed gray'};
  padding-top: 0.25rem;
`;

const CardGrouping = styled.div`
  padding-block-start: 2rem;
  background-color: ${(props) => props.$background || '#ffffff'};
`;

const CardGroupingHeader = styled.div`
  font-size: 1.125rem;
  color: #666666;

  @media (max-width: 768px) {
    text-align: center;
    max-width: 100%;
  }
`;

const CardGroupingContainer = styled.div`
  max-width: 2200px;
  margin: 0 auto;
  padding: 0 4rem;

  @media (max-width: 1440px) {
    padding: 0 2rem;
  }

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  max-width: 2200px;
  margin: 0 auto;

  @media (max-width: 2200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 1640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  & a {
    text-decoration: none;
  }
`;

const DateLine = styled.div`
  font-style: italic;
  font-size: 0.75rem;
  line-height: 1.1;
  color: ${(props) => props.$text || '#fcd70b'};
  margin-right: 0.25rem;
`;

const HeadlineWrapper = styled.div`
  flex-grow: 1;
`;

const Headline = styled.h3`
  font-size: 1.25rem;
  line-height: 1.3;
  font-weight: 700;
  color: ${(props) => props.$text || '#ffffff'};
  margin: 0;
  flex-grow: 1;
  overflow: hidden;
  padding-inline-end: 0.5rem;
`;

const Subhead = styled.h4`
  font-size: 0.875rem;
  line-height: 1.5;
  font-weight: 400;
  color: ${(props) => props.$text || '#d9d9d9'};
  padding-inline-end: 0.5rem;
  margin: 1rem 0 0;
`;

const PhenomScore = styled.div`
  line-height: 1.1;
  color: ${(props) => props.$text || '#fcd70b'};
  font-weight: 700;
`;

const ScoreText = styled.div`
  font-size: 0.75rem;
  position: relative;
  &:hover {
    ${Tooltip} {
      color: #0e4c60;
      top: 1.375rem;
      display: block;
      right: 0;
    }
  }
`;

const SpeakingSign = styled.div`
  height: 1rem;
  width: 1rem;
  display: inline-block;
  vertical-align: middle;

  & svg {
    color: #fcd70b;
  }

  &:hover {
    ${Tooltip} {
      left: 1rem;
      display: block;
      color: #0e4c60;
      width: calc(100% - 4rem);
      top: calc(100% - 1rem);
    }
  }
`;

const TypeBadge = styled.div`
  color: ${(props) => props.$text || '#fcd70b'};
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.1;
  display: inline-block;
`;

const RenderCardsWrapper = styled.div`
  padding: 2rem 4rem;
  isolation: isolate;

  @media (max-width: 1440px) {
    padding: 2rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CardTopLeft = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const ClusteredCardContainer = styled(CardContainer)`
  position: relative;
  transition: transform 0.2s ease;

  &::after,
  &::before {
    content: '';
    position: absolute;
    border-radius: 1rem;
    pointer-events: none;
    box-shadow: 3px 4px 4px 0px #00000040;
    height: 100%;
    width: 100%;
    background-color: ${(props) =>
      props.$upcomingHearing ? '#ffffff' : '#0e4c60'};
    transition: transform 0.2s ease;
  }

  &::after {
    top: 10px;
    left: 10px;
  }

  &::before {
    top: 20px;
    left: 20px;
  }

  &:hover {
    transform: translateY(-5px);
    transition: transform 0.2s ease;
  }

  &:hover::after {
    transform: translateY(5px);
    transition: transform 0.2s ease;
  }

  &:hover::before {
    transform: translateY(10px);
    transition: transform 0.2s ease;
  }
`;

const ClusterLabel = styled.div`
  color: #0e4c60;
  margin-block-start: ${(props) => (props.$clusterCard ? '2rem' : '1rem')};
`;

const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
`;

const DialogContent = styled.div`
  border-radius: 0.5rem;
  padding: 2rem 1rem;
  max-width: 31.25rem;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  color: #0e4c60;
  background-color: ${(props) => props.$background || '#ffffff'};
`;

const DialogTitle = styled.h2`
  margin-top: 0;
  padding-bottom: 1rem;
  font-size: 1rem;
  font-weight: 400;
`;

const DialogClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0e4c60;
`;

const DialogCardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const DialogCard = styled.div`
  position: relative;
`;

const ScoreBadge = styled.span`
  background-color: ${(props) =>
    props.$category === 'High'
      ? '#DB0000'
      : props.$category === 'Medium'
      ? '#E67300'
      : '#cccccc'};
  color: ${(props) =>
    props.$category === 'High'
      ? '#ffffff'
      : props.$category === 'Medium'
      ? '#ffffff'
      : '#212121'};
  padding: 0.5rem 0.75rem;
  border-radius: 0.625rem;
  text-transform: uppercase;
  text-align: center;
  position: relative;
  white-space: nowrap;

  & > span:last-child {
    opacity: 0;
    transition: opacity 0.2s ease;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  & > span:first-child {
    transition: opacity 0.2s ease;
  }

  /* Make hover effect work on both CardContainer and DialogContent */
  ${CardContainer}:hover &,
	${DialogCard}:hover & {
    & > span:first-child {
      opacity: 0;
    }

    & > span:last-child {
      opacity: 1;
    }
  }
`;

export const voteOutcome = (tipsheet) => {
  var tile_json = tipsheet.tipsheet_tile_json;
  var today = new Date();
  var days_ago = new Date(new Date().setDate(today.getDate() - 3));

  if (tile_json?.hasOwnProperty('vote_outcome')) {
    let voteClass = '';
    let toolTipText = '';
    let kkey = Date.now();
    if (tile_json.vote_outcome?.slice(0, 3) === 'pas') {
      voteClass = 'vote-pass-card';
      toolTipText = 'motion passed';
    } else if (tile_json.vote_outcome?.slice(0, 3) === 'fai') {
      voteClass = 'vote-fail-card';
      toolTipText = 'motion failed';
    } else if (
      tile_json.tipsheet_type === 'bill' &&
      tipsheet.tile_bill_num?.toLowerCase() !== 'informational' &&
      new Date(tipsheet.tile_date) >= days_ago
    ) {
      voteClass = 'vote-not-available';
      toolTipText =
        'Official vote results have not yet been obtained. Tip Sheet will be updated as soon as possible.';
    } else {
      return;
    }
    return (
      <AbbreviationText key={kkey}>
        <span className={voteClass}>
          <Tooltip>{toolTipText}</Tooltip>
        </span>
      </AbbreviationText>
    );
  }
};

// returns the first "lead author" if "bill_authors" property exists, otherwise, nothing
export const getAuthor = (tile) => {
  if (tile?.hasOwnProperty('bill_authors')) {
    for (let idx in tile.bill_authors) {
      if (tile.bill_authors[idx].contribution.toLowerCase() === 'lead author') {
        return (
          '(' +
          tile.bill_authors[idx].first +
          ' ' +
          tile.bill_authors[idx].last +
          ')'
        );
      }
    }
  }
};

export const getDuration = (duration_str) => {
  const duration = Number(duration_str);
  let response = '';
  if (duration === 0) return response;
  let seconds = Number(duration_str);
  let minutes = Math.floor(seconds / 60);
  let hours = 0;
  if (minutes > 60) {
    hours = Math.floor(minutes / 60);
    minutes = minutes % 60;
  }

  if (hours > 0) response += hours.toString() + 'h ';
  if (minutes === 0) response += seconds.toString() + ' sec';
  else response += minutes.toString() + ' min';
  return response;
};

export const getAnnotatedScore = (tipsheet, upcomingHearing) => {
  const content_obj = tipsheet?.content;
  const score = tipsheet?.score || 0;

  // Get score category text
  const getScoreCategory = (score) => {
    if (score >= 20) return 'High';
    if (score >= 10) return 'Medium';
    return 'Low';
  };

  let score_text = [];
  let hasContentObjData = false;
  if (content_obj && typeof content_obj === 'object') {
    score_text = Object.keys(content_obj)
      .map((key) => {
        if (content_obj[key]['score'] && content_obj[key]['score'] > 0) {
          let tipText = ' +' + content_obj[key]['score'].toString() + ': ';
          if (content_obj[key]?.hasOwnProperty('description')) {
            tipText += content_obj[key]['description'];
          }
          return [tipText, content_obj[key]['score']];
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b[1] - a[1])
      .map((t, i) => <li key={i}>{t[0]}</li>);
    hasContentObjData = score_text.length > 0;
  } else if (score > 0) {
    score_text = [<li key="score">+{score}</li>];
  }

  return (
    <ScoreText key={tipsheet?.jsonid} $text={upcomingHearing ? '#ffffff' : ''}>
      <ScoreBadge $category={getScoreCategory(score)}>
        <span>{getScoreCategory(score)}</span> <span>{score}</span>
      </ScoreBadge>
      {hasContentObjData && (
        <Tooltip>
          <ul>{score_text}</ul>
        </Tooltip>
      )}
    </ScoreText>
  );
};

export const formatClusterName = (clusterName) => {
  if (!clusterName || clusterName === 'None') return '';

  // Remove "Geo " prefix and format the name
  if (clusterName.startsWith('Geo ') || clusterName.startsWith('geo_')) {
    const nameWithoutGeo = clusterName.substring(4); // Remove first 4 chars ("Geo ")

    // Convert to title case (capitalize first letter of each word)
    return nameWithoutGeo
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // For other cluster names, just replace underscores with spaces
  return clusterName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper to get subhead(s) for a card based on legislators/personas
export const getSubheads = (card, legislators, upcomingHearing = false) => {
  const personas = card?.tipsheet_tile_json?.personas;
  if (
    Array.isArray(legislators) &&
    legislators.length > 0 &&
    Array.isArray(personas)
  ) {
    const legislatorPids = legislators.map((l) => l.pid);
    const matchingPersonas = personas
      .filter((p) => legislatorPids.includes(p.pid) && p.sub_headline)
      .sort((a, b) => (b.pscore || 0) - (a.pscore || 0));
    if (matchingPersonas.length > 0) {
      const persona = matchingPersonas[0];
      return (
        <Subhead $text={upcomingHearing ? '#212121' : ''} key={persona.pid}>
          {persona.sub_headline}
        </Subhead>
      );
    }
  }
  // former fallback to default_subhead
  return null;
};

// Returns true for cards the backend has flagged as past the hearing but awaiting transcription
const isPastPrehearing = (card) =>
  card.tipsheet_tile_json?.type_display_str === 'Awaiting Transcription';

// Helper to format tile_date_line to abbreviated day/month names
const formatTileDateLine = (dateStr) => {
  if (!dateStr) return '';
  // Try to parse the date string
  const parsedDate = new Date(dateStr);
  if (isNaN(parsedDate)) return dateStr;
  // Format: Mon, Nov 10, 2025
  const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
  return parsedDate.toLocaleDateString('en-US', options);
}

// main code to render a group of tipsheet tiles (cards)
export const RenderCards = ({
  cards,
  additionalSelectionFunction = null,
  upcomingHearing = false, // all tiles with a _T3 suffix ('Pre-Hearing', 'Awaiting Transcription')
  onClusterClick = null,
  pidToName,
  clusterBy,
  legislators,
}) => {
  if (!cards) return null;
  return (
    <Cards>
      {cards.map((card) => {
        // Determine if this is a cluster representative card
        const isClusterCard = card.isCluster;

        // Choose the appropriate container component
        const ContainerComponent = isClusterCard
          ? ClusteredCardContainer
          : CardContainer;

        const handleCardClick = (e) => {
          // If it's a cluster card, we want to show the dialog instead of navigating
          if (isClusterCard && onClusterClick) {
            e.preventDefault();
            onClusterClick(card.clusterName, card.week);
          }
        };

        const awaitingTranscription = isPastPrehearing(card);

        return (
          <div style={{ position: 'relative' }} key={card.key || card.id}>
            <Link
              to={`/${card.id}`}
              target="_blank"
              onClick={handleCardClick}
              style={{ textDecoration: 'none' }}
            >
              <ContainerComponent $upcomingHearing={upcomingHearing}>
                <CardContents
                  $background={awaitingTranscription ? '#fef9c3' : upcomingHearing ? '#ffffff' : ''}
                  $border={awaitingTranscription ? '1px solid #eab308' : upcomingHearing ? '1px solid #cccccc' : ''}
                >
                  <CardTop>
                    <CardTopLeft>
                      {(card.tipsheet_tile_json?.type_display_str) && (
                        <TypeBadge $text={awaitingTranscription ? '#92400e' : upcomingHearing ? '#212121' : ''}>
                          {card.tipsheet_tile_json?.type_display_str}
                        </TypeBadge>
                      )}
                      <DateLine $text={awaitingTranscription ? '#92400e' : upcomingHearing ? '#212121' : ''}>
                          {formatTileDateLine(card.tile_date_line)}
                      </DateLine>
                    </CardTopLeft>
                    <PhenomScore $text={upcomingHearing ? '#0E4C60' : ''}>
                      {getAnnotatedScore(
                        card.tipsheet_tile_json,
                        upcomingHearing
                      )}
                    </PhenomScore>
                  </CardTop>
                  <HeadlineWrapper>
                    <Headline $text={upcomingHearing ? '#0E4C60' : ''}>
                      {card.headline}
                    </Headline>
                    {getSubheads(card, legislators, upcomingHearing)}
                  </HeadlineWrapper>
                  <CardDetails
                    $text={upcomingHearing ? '#0E4C60' : ''}
                    $border={upcomingHearing ? '1px dashed #3D3D3D' : ''}
                  >
                    {card.tile_committee !== 'None' &&
                      `${card.tile_committee}, `}
                    {getDuration(card.tipsheet_tile_json?.video_duration) && (
                      <>
                        {getDuration(card.tipsheet_tile_json?.video_duration)}
                        {card.tile_bill_num?.toLowerCase().slice(0, 3) !==
                          'inf' && ', '}
                      </>
                    )}
                    {card.tile_bill_num?.toLowerCase().slice(0, 3) !== 'inf' &&
                      `${card.tile_bill_num} `}
                    {getAuthor(card.tipsheet_tile_json)} {voteOutcome(card)}
                    {additionalSelectionFunction &&
                      additionalSelectionFunction(card)}
                  </CardDetails>
                </CardContents>
              </ContainerComponent>
            </Link>
            {card.clusterName && clusterBy !== 'none' && (
              <ClusterLabel $clusterCard={isClusterCard}>
                {clusterBy === 'legislator'
                  ? pidToName[card.clusterName]
                  : formatClusterName(String(card.clusterName))}
              </ClusterLabel>
            )}
          </div>
        );
      })}
    </Cards>
  );
};

// Dialog component for showing cluster cards
const ClusterDialog = ({
  isOpen,
  onClose,
  title,
  subTitle,
  cards,
  additionalSelectionFunction,
  pidToName,
  clusterBy,
  legislators,
}) => {
  if (!isOpen) return null;

  const upcomingHearing = cards[0]?.week > 9999; // all tiles with a _T3 suffix ('Pre-Hearing', 'Awaiting Transcription')

  return (
    <DialogOverlay onClick={onClose}>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        $background={upcomingHearing ? '#fafafa' : ''}
      >
        <DialogClose onClick={onClose}>
          <CloseIcon />
        </DialogClose>
        <DialogTitle>
          {clusterBy === 'legislator'
            ? pidToName[title]
            : formatClusterName(String(title))}
        </DialogTitle>
        <p>{subTitle} Tip Sheets</p>
        <DialogCardsList>
          {cards.map((card) => {
            const awaitingTranscription = isPastPrehearing(card);
            return (
            <DialogCard key={card.id}>
              <Link
                to={`/${card.id}`}
                target="_blank"
                style={{ textDecoration: 'none' }}
              >
                <CardContents
                  style={{ marginBottom: '1rem' }}
                  $background={awaitingTranscription ? '#fef9c3' : upcomingHearing ? '#ffffff' : ''}
                  $border={awaitingTranscription ? '1px solid #eab308' : upcomingHearing ? '1px solid #cccccc' : ''}
                >
                  <CardTop>
                    <CardTopLeft>
                      {(card.tipsheet_tile_json?.type_display_str) && (
                        <TypeBadge $text={awaitingTranscription ? '#92400e' : upcomingHearing ? '#212121' : ''}>
                          {card.tipsheet_tile_json?.type_display_str}
                        </TypeBadge>
                      )}
                      <DateLine $text={awaitingTranscription ? '#92400e' : upcomingHearing ? '#212121' : ''}>
                        {formatTileDateLine(card.tile_date_line)}
                      </DateLine>
                    </CardTopLeft>
                    <PhenomScore $text={upcomingHearing ? '#0E4C60' : ''}>
                      {getAnnotatedScore(card.tipsheet_tile_json)}
                    </PhenomScore>
                  </CardTop>
                  <HeadlineWrapper>
                    <Headline $text={upcomingHearing ? '#0E4C60' : ''}>
                      {card.headline}
                    </Headline>
                    {getSubheads(card, legislators, upcomingHearing)}
                  </HeadlineWrapper>
                  <CardDetails
                    $text={upcomingHearing ? '#0E4C60' : ''}
                    $border={upcomingHearing ? '1px dashed #3D3D3D' : ''}
                  >
                    {card.tile_committee !== 'None' &&
                      `${card.tile_committee}, `}
                    {getDuration(card.tipsheet_tile_json.video_duration) && (
                      <>
                        {getDuration(card.tipsheet_tile_json.video_duration)}
                        {card.tile_bill_num.toLowerCase().slice(0, 3) !==
                          'inf' && ', '}
                      </>
                    )}
                    {card.tile_bill_num.toLowerCase().slice(0, 3) !== 'inf' &&
                      `${card.tile_bill_num} `}
                    {getAuthor(card.tipsheet_tile_json)} {voteOutcome(card)}
                    {additionalSelectionFunction &&
                      additionalSelectionFunction(card)}
                  </CardDetails>
                </CardContents>
              </Link>
            </DialogCard>
          );
          })}
        </DialogCardsList>
      </DialogContent>
    </DialogOverlay>
  );
};

export default function Stories({
  legislators,
  searchActive,
  coverageMessage,
  coverageActive,
  isLoading = false,
  clusterMap,
  weekToWeekStr,
  pidToName,
  clusterBy,
  weekToStoryCount,
}) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clusterCards, setClusterCards] = useState([]);

  // Handle cluster card click
  const handleClusterClick = useCallback(
    (clusterName, week) => {
      // Use clusterMap to get the array of cards for this week and clusterName
      if (clusterMap && clusterMap.has(week)) {
        const clusterMapForWeek = clusterMap.get(week);
        if (clusterMapForWeek && clusterMapForWeek.has(clusterName)) {
          const clusterItems = clusterMapForWeek.get(clusterName);

          // Sort by score (highest first)
          const sortedCluster = [...clusterItems].sort(
            (a, b) =>
              (b.tipsheet_tile_json?.score || 0) -
              (a.tipsheet_tile_json?.score || 0)
          );

          setSelectedCluster(clusterName);
          setClusterCards(sortedCluster);
          setIsDialogOpen(true);
          return;
        }
      }
      // fallback: empty dialog if not found
      setSelectedCluster(clusterName);
      setClusterCards([]);
      setIsDialogOpen(true);
    },
    [clusterMap]
  );

  if (coverageMessage && coverageActive) return null;

  if (isLoading) {
    return <StoriesSkeleton />;
  }

  const speakingSign = (card) => {
    const personas = card?.tipsheet_tile_json?.personas;
    if (!personas) return null;
    const lawmakerSpeakers = legislators.map((lawmaker) => lawmaker.pid);
    const hasSpeaker = personas
      .filter((persona) => persona.p_type.includes('speaker'))
      .map((persona) => persona.pid)
      .some((pid) => lawmakerSpeakers.includes(pid));

    if (hasSpeaker) {
      return (
        <SpeakingSign key={`${card.tipsheet_tile_json.jsonid}_speaker`}>
          <SpeakingIcon />
          <Tooltip>
            At least one of the selected lawmakers from the filters above is a
            speaker at this event and has transcribed lines captured in the
            transcript.
          </Tooltip>
        </SpeakingSign>
      );
    }
    return null;
  };

  // Helper to get all weeks sorted descending
  const sortedWeeks = clusterMap
    ? Array.from(clusterMap.keys()).sort((a, b) => b - a)
    : [];

  return (
    <>
      <ClusterDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={selectedCluster}
        subTitle={clusterCards.length}
        cards={clusterCards}
        additionalSelectionFunction={legislators ? speakingSign : null}
        pidToName={pidToName}
        clusterBy={clusterBy}
        legislators={legislators}
      />
      {clusterMap && sortedWeeks.length > 0 ? (
        <>
          {sortedWeeks.map((week, groupIndex) => {
            const clusterMapForWeek = clusterMap.get(week);
            const cardGroup = Array.from(clusterMapForWeek.entries()).map(
              ([clusterName, arr]) => ({
                ...arr[0],
                clusterName,
                isCluster: arr.length > 1,
                key: `${week}-${clusterName}-${arr[0].id}`,
              })
            );

            const upcomingHearing = week > 9999; // all tiles with a _T3 suffix ('Pre-Hearing', 'Awaiting Transcription')
            const prettyWeek =
              weekToWeekStr && weekToWeekStr[week] ? weekToWeekStr[week] : week;

            return (
              <CardGrouping
                key={groupIndex}
                $background={upcomingHearing ? '#fafafa' : ''}
              >
                <CardGroupingContainer>
                  <CardGroupingHeader>
                    {upcomingHearing ? (
                      <>
                        <strong>Predictions:</strong> Upcoming Week of{' '}
                        {prettyWeek}{' '}
                      </>
                    ) : (
                      `Week of ${prettyWeek} `
                    )}
                    ({weekToStoryCount[week]} Tip Sheet
                    {weekToStoryCount[week] > 1 ? 's' : ''})
                  </CardGroupingHeader>
                </CardGroupingContainer>
                <RenderCardsWrapper
                  $padding={upcomingHearing ? 'upcoming' : ''}
                >
                  <RenderCards
                    cards={cardGroup}
                    additionalSelectionFunction={
                      legislators ? speakingSign : null
                    }
                    upcomingHearing={upcomingHearing}
                    onClusterClick={handleClusterClick}
                    pidToName={pidToName}
                    clusterBy={clusterBy}
                    legislators={legislators}
                  />
                </RenderCardsWrapper>
              </CardGrouping>
            );
          })}
        </>
      ) : (
        <CardGrouping>
          <CardGroupingContainer>
            <CardGroupingHeader>
              No results found for your search term.
            </CardGroupingHeader>
            <div style={{ padding: '2rem 0' }}>
              Please try a different search term or check your filters.
            </div>
          </CardGroupingContainer>
        </CardGrouping>
      )}
    </>
  );
}
